import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import createBbox from '@turf/bbox'
import { featureCollection } from '@turf/helpers'
import * as geojson from 'geojson'
import memoize from 'fast-memoize'
import { getResolution } from 'h3-js'
import useDebounce from 'react-use/lib/useDebounce'
import Arc from '~/components/map/arc'
import GeoJSON from '~/components/map/geoJson'
import Heatmap from '~/components/map/heatmap'
import Map from '~/components/map/map'
import { ClusterPosition, NeoClusterPosition } from '~/components/map/mapPosition'
import {
  AppError,
  fromUnknownError,
  truthy,
} from '~/prix'
import Handle from '~/prix/react/components/handle'
import useAction from '~/prix/react/hooks/action'
import useAPI from '~/prix/react/hooks/api'
import useConcurrentActions, { ActionCall } from '~/prix/react/hooks/concurrentActions'
import h3ResolutionResolve from '~/prix/utils/h3ResolutionResolve'
import { cellToMapLatLngLiteral } from '~/prix/utils/h3Coordinates'
import actionDefinitions from '../actionDefinitions'
import geoListDefinition from '../geo/list/geoList.definition'
import variableListDefinition from '~/packages/general/variable/list/variableList.definition'
import recommendedLayerForVariable, {
  getDefaultTimeForVariable,
  normalizeLayerSpaceForVariable,
} from '~/packages/general/variable/recommendedLayerForVariable.util'
import { compatibleSpaceChildren, spaceTypeSingularLabels } from '~/packages/general/variable/spaceTime.data'
import { Layer, Space, SpaceChildrenItem, SpaceGeoStandardType } from '~/packages/general/variable/spaceTime.types'
import { normalizeLayersTimeAfterGeographyChange } from './layerGeographyChange.util'
import { Conclusion, Insight, VariableWithConclusion } from '~/packages/general/variable/variables.typing'
import ClusterList, { SelectedCluster } from './clusterList.component'
import InfoWindow from './infoWindow.component'
import statsAndGeoToFeatureCollection, {
  FeatureCollectionWithInfoAndStyle,
  FeatureWithInfoAndStyle,
  GeoItem,
  StatsItem,
} from './statsAndGeoToFeatureCollection.util'
import { borderOnlyStyle, StyleForLayer } from './styleForLayer.util'
import { getColorMap } from '~/design'
import LegendsContainer from './legendsContainer.component'
import { toastError } from '~/components/toasts'
import { ReportWithLayer } from './reports'
import MapReport from './report.component'
import { useMenuControls } from './hooks/useMenuControls'
import { useStyles } from './hooks/useStylesForLegends'
import NeoCluster from '~/components/map/neoCluster'
import { ScreenDefault } from '~/components/baseInterface'
import InsightsModal, { SelectedInsight } from './insightsModal.component'
import recommendedVariableForUser from '~/packages/general/variable/recommendedVariableForUser.util'
import { hasAnyUserRole } from '~/utils/shared/userProfileCompatibility'
import { isVariableLayer } from './clusterListInput.util'
import { partitionStatsItemsByGeoSelection, summarizeLegendStatsItems } from './statsGeoSelection.util'
import {
  buildStatsSpaceForUserScope,
  getPreferredSpaceFromGeneralSettings,
} from './userDelimitationScope.util'

export interface SentinelaProps {
  selectedInsight: SelectedInsight | null
  readySelectedInsight: SelectedInsight | null
  layers: Layer[]
  baseLayers: Layer[]
  onChangeSelectedInsight: React.Dispatch<React.SetStateAction<SelectedInsight | null>>
  onChangeBaseLayers: React.Dispatch<React.SetStateAction<Layer[]>>
}

export default function MainInterface({
  selectedInsight,
  readySelectedInsight,
  layers,
  baseLayers,
  onChangeSelectedInsight: setSelectedInsight,
  onChangeBaseLayers: setBaseLayers,
}: SentinelaProps) {
  const isDebouncingInsight = selectedInsight !== readySelectedInsight

  const { context, isLoading: isLoadingAPI } = useAPI()
  const preferredGeneralSettings = context.user?.activeClient?.settings?.general
  const preferredCoordinates = preferredGeneralSettings?.preferredCoordinates

  const preferredSpaceInput = useMemo(
    () => ({
      preferredSpaceDelimitationType: preferredGeneralSettings?.preferredSpaceDelimitationType,
      preferredSpaceDelimitationId: preferredGeneralSettings?.preferredSpaceDelimitationId,
    }),
    [
      preferredGeneralSettings?.preferredSpaceDelimitationId,
      preferredGeneralSettings?.preferredSpaceDelimitationType,
    ],
  )

  const variablesResult = useAction(variableListDefinition, preferredSpaceInput, [preferredSpaceInput], {
    autoLoad: Boolean(context.user) && !isLoadingAPI,
    staleWhileRevalidate: true,
  })

  const variables: Array<VariableWithConclusion> = variablesResult.result?.items ?? []

  const lastVariableSpaceSignatureRef = useRef<string | null>(null)

  const [stableCustomSpace, setStableCustomSpace] = useState<Space | null>(null)
  const [report, setReport] = useState<ReportWithLayer | null>(null)
  const [legendStyles, setLegendStyles] = useState<StyleForLayer[]>([])
  const { is3D, map3DOption, setMap3DOption, setMapType, mapType } = useMenuControls({
    baseLayers,
    layers,
  })
  const [isInsightsModalOpen, setIsInsightsModalOpen] = useState<boolean | null>(null)
  const [isFirstLoadDone, setIsFirstLoadDone] = useState<boolean>(false)

  const companyCategoryKeys = useMemo(() => new Set([]) as Set<string>, [])
  const companyVariableKeys = useMemo(() => new Set([]) as Set<string>, [])

  const [mapPosition, setMapPosition] = useState<
    | {
        center: google.maps.LatLngLiteral
        zoom: number
      }
    | {
        bounds: google.maps.LatLngBoundsLiteral
      }
    | null
  >(null)
  const [selectedCluster, setSelectedCluster] = useState<SelectedCluster | null>(null)
  const [pendingOpenInsightsModal, setPendingOpenInsightsModal] = useState(false)
  const skipNextAutoOpenRef = useRef(false)

  const isAdminUser = useMemo(
    () => hasAnyUserRole(context.user, ['systemAdministrator', 'administrator', 'manager']),
    [context.user],
  )

  const isStateManager = useMemo(
    () => hasAnyUserRole(context.user, ['stateManager']),
    [context.user],
  )

  const preferredMapCenter = useMemo(() => {
    if (!preferredCoordinates || preferredCoordinates.length !== 2) {
      return null
    }

    return {
      lat: preferredCoordinates[1],
      lng: preferredCoordinates[0],
    }
  }, [preferredCoordinates])

  const stateScope = useMemo(() => {
    if (!isStateManager || !preferredGeneralSettings) {
      return { stateId: null as number | null, stateName: null as string | null }
    }

    if (preferredGeneralSettings.preferredSpaceDelimitationType !== 'state') {
      return { stateId: null as number | null, stateName: null as string | null }
    }

    return {
      stateId: preferredGeneralSettings.preferredSpaceDelimitationId,
      stateName: preferredGeneralSettings.preferredSpaceDelimitationTitle,
    }
  }, [isStateManager, preferredGeneralSettings])

  const preferredSpace = useMemo(
    () => getPreferredSpaceFromGeneralSettings(preferredGeneralSettings),
    [
      preferredGeneralSettings?.preferredSpaceChildrenType,
      preferredGeneralSettings?.preferredSpaceDelimitationId,
      preferredGeneralSettings?.preferredSpaceDelimitationTitle,
      preferredGeneralSettings?.preferredSpaceDelimitationType,
    ],
  )

  const layerSpaceNormalizationOptions = useMemo(
    () => ({
      hasPermissions: isAdminUser,
      stateId: stateScope.stateId,
      stateName: stateScope.stateName,
      preferredSpace,
    }),
    [
      isAdminUser,
      preferredSpace,
      stateScope.stateId,
      stateScope.stateName,
    ],
  )

  const normalizeLayerSpace = useCallback(
    (layer: Layer) => normalizeLayerSpaceForVariable(layer, layerSpaceNormalizationOptions),
    [layerSpaceNormalizationOptions],
  )

  const handleLocationWithoutCustomSpace = useCallback(
    (layer: Layer): Space => {
      try {
        if (
          layer.space.delimitationType === 'custom' &&
          !layer.space.delimitationCustom &&
          stableCustomSpace &&
          stableCustomSpace.delimitationType !== 'custom'
        ) {
          layer.space = {
            ...layer.space,
            childrenType: stableCustomSpace?.childrenType,
            delimitationType: stableCustomSpace?.delimitationType as SpaceGeoStandardType,
            delimitationTitle: stableCustomSpace?.delimitationTitle,
            delimitationId: stableCustomSpace?.delimitationId,
          } as Space
          return layer.space
        }
        return layer.space
      } catch (error) {
        console.error('Erro ao tratar localização customizada:', error)
        return layer.space
      }
    },
    [stableCustomSpace],
  )

  const normalizeSpaceForGov = useCallback((space: Space): Space => {
    const delimitationType =
      String(space.delimitationType) === 'city' ? 'municipality' : space.delimitationType
    const childrenType =
      String(space.childrenType) === 'city' ? 'municipality' : space.childrenType

    return {
      ...space,
      delimitationType: delimitationType as Space['delimitationType'],
      childrenType: childrenType as Space['childrenType'],
    } as Space
  }, [])

  const layersRequests: ActionCall[] = useMemo(
    () =>
      layers.map((layer, index): ActionCall => {
        if (!isVariableLayer(layer)) {
          return null as never
        }

        const actionDefinition = actionDefinitions.find(
          actionDefinition => actionDefinition.key === layer.variable.actionDefinitionKey,
        ) as ActionCall['actionDefinition'] | undefined

        if (!actionDefinition) {
          throw new Error(`Action definition not found for variable ${layer.variable.key}`)
        }

        if (
          readySelectedInsight &&
          readySelectedInsight.baseLayerIndex === index &&
          readySelectedInsight.preview?.statsItems
        ) {
          return null as never
        }

        const normalizedSpace = normalizeSpaceForGov(handleLocationWithoutCustomSpace(layer))
        const space = buildStatsSpaceForUserScope(normalizedSpace, preferredSpace)
        return {
          actionDefinition,
          input: {
            variable: layer.variable,
            filter: layer.variable.filter ?? undefined,
            highlight: layer.variable.highlight ?? undefined,
            space,
            time: layer.time,
            analysis: {
              timeSeries: layer.variable.timeSeries,
              include: {
                items: true,
                prediction: 'auto',
                alerts: false,
                insights: true,
                components: false,
              },
            },
          },
          options: {
            staleWhileRevalidate: true,
          },
        }
      }),
    [
      layers,
      preferredSpace,
      readySelectedInsight,
      handleLocationWithoutCustomSpace,
      normalizeSpaceForGov,
    ],
  )

  const layerRequestsResult = useConcurrentActions(layersRequests, [layersRequests])

  // Geo
  const distinctGeoRequests = useMemo(
    () =>
      layers.map((layer, index) => {
        if (layer.space.delimitationType === 'custom' && layer.space.childrenType === 'item') {
          return null as never
        }

        if (
          readySelectedInsight &&
          readySelectedInsight.baseLayerIndex === index &&
          readySelectedInsight.preview?.geoItems
        ) {
          return null as never
        }

        let space = normalizeSpaceForGov(layer.space)
        const compatibleChildren =
          compatibleSpaceChildren[space.delimitationType as keyof typeof compatibleSpaceChildren]

        if (!compatibleChildren) {
          return null as never
        }

        if (space.childrenType === 'item') {
          const fallbackChildrenType = compatibleChildren[compatibleChildren.length - 1]
          if (!fallbackChildrenType) {
            return null as never
          }

          space = {
            ...space,
            childrenType: fallbackChildrenType as SpaceGeoStandardType,
          }
        }

        return {
          actionDefinition: geoListDefinition,
          input: {
            spaces: [space],
          },
          options: {
            staleWhileRevalidate: true,
          },
        }
      }),
    [layers, readySelectedInsight, normalizeSpaceForGov],
  )

  const geoRequestsResult = useConcurrentActions(distinctGeoRequests, [distinctGeoRequests])

  // Min and max values
  const ranges = useMemo(
    () =>
      layers.map((layer, index) => {
        const statsItems: StatsItem[] | null =
          readySelectedInsight?.preview?.statsItems ??
          layerRequestsResult.states[index]?.result?.items
        if (!statsItems || !geoRequestsResult) {
          return {
            min: 0,
            max: 0,
            total: 0,
            totalValueCount: 0,
            totalNumerator: 0,
            totalDenominator: 0,
            outsideSelectionTotal: 0,
            outsideSelectionTotalNumerator: 0,
            outsideSelectionTotalDenominator: 0,
          }
        }

        // geo items from insight or from request
        const geoItems =
          (readySelectedInsight?.preview?.geoItems ??
            geoRequestsResult.states[index]?.result?.items) as GeoItem[] | null | undefined

        const unitFormat = isVariableLayer(layer) ? layer.variable.unitFormat : undefined
        const statsPartition = partitionStatsItemsByGeoSelection({
          statsItems,
          geoItems,
          childrenType: layer.space.childrenType,
        })

        const visibleMetrics = summarizeLegendStatsItems({
          statsItems: statsPartition.visibleStatsItems,
          unitFormat,
          visibleGeoItemsCount: geoItems?.length,
        })

        const totalMetrics = summarizeLegendStatsItems({
          statsItems,
          unitFormat,
          visibleGeoItemsCount: geoItems?.length,
        })

        const outsideSelectionMetrics = summarizeLegendStatsItems({
          statsItems: statsPartition.outsideSelectionStatsItems,
          unitFormat,
        })

        return {
          min: visibleMetrics.min,
          max: visibleMetrics.max,
          total: totalMetrics.total,
          totalValueCount: totalMetrics.totalValueCount,
          totalNumerator: totalMetrics.totalNumerator,
          totalDenominator: totalMetrics.totalDenominator,
          outsideSelectionTotal: outsideSelectionMetrics.total,
          outsideSelectionTotalNumerator: outsideSelectionMetrics.totalNumerator,
          outsideSelectionTotalDenominator: outsideSelectionMetrics.totalDenominator,
        }
      }),
    [layers, layerRequestsResult, geoRequestsResult, readySelectedInsight],
  )

  const estimatedItemsCountPerLayer = useMemo(
    () =>
      ranges.map(range => {
        const byWeightedMeanCount = Number((range as { totalValueCount?: number }).totalValueCount)
        if (Number.isFinite(byWeightedMeanCount) && byWeightedMeanCount > 0) {
          return Math.round(byWeightedMeanCount)
        }
        const byDenominator = Number(range.totalDenominator)
        if (Number.isFinite(byDenominator) && byDenominator > 0) {
          return Math.round(byDenominator)
        }
        const byTotal = Number(range.total)
        return Number.isFinite(byTotal) ? Math.round(byTotal) : 0
      }),
    [ranges],
  )

  // Styles
  const { hexagonStyles, styles, valueFormatters } = useStyles({ layers, ranges })

  useEffect(() => {
    setLegendStyles(styles)
  }, [styles])

  // Feature collections
  const featureCollections = useMemo(
    () =>
      layers.map((layer, index) => {
        if (layer.space.delimitationType === 'custom' && layer.space.childrenType === 'item') {
          return featureCollection([
            {
              ...layer.space.delimitationCustom,
              properties: {
                ...(layer.space.delimitationCustom as geojson.Feature)?.properties,
                name: layer.space.delimitationTitle,
                geoType: 'custom',
                ...borderOnlyStyle.mapStyle(),
              },
            },
          ]) as FeatureCollectionWithInfoAndStyle
        }

        const statsItems: StatsItem[] | null =
          readySelectedInsight?.preview?.statsItems ??
          layerRequestsResult.states[index]?.result?.items

        if (!statsItems) return null

        // @TODO: Remove this when the backend for neighborhood is fixed
        const geoRequestsResultItems = geoRequestsResult.states[index]?.result?.items
        const hasGeoItemWithShape = geoRequestsResultItems
          ? geoRequestsResultItems.some(item => item.shape)
          : false
        const geoRequestsResultItemsFiltered =
          geoRequestsResultItems && hasGeoItemWithShape
            ? geoRequestsResultItems.filter(item => item.shape)
            : geoRequestsResultItems

        const geoItems = readySelectedInsight?.preview?.geoItems ?? geoRequestsResultItemsFiltered

        if (!geoItems) return null
        return statsAndGeoToFeatureCollection(statsItems, geoItems as unknown as GeoItem[], {
          fillStatsItem: layer.space.childrenType !== 'item' ? { value: 0 } : { value: undefined },
          mapStyle: styles[index].mapStyle,
        })
      }),
    [layers, layerRequestsResult, geoRequestsResult, readySelectedInsight, styles],
  )

  const resolutions = useMemo(
    () =>
      layers.map(
        layer =>
          (layer.space as SpaceChildrenItem).childrenH3Resolution ||
          h3ResolutionResolve(layer.space.delimitationType),
      ),
    [layers],
  )

  const heatmapAndClusterWeightedPositions = useMemo(
    () =>
      layers.map((layer, index): ClusterPosition[] | null => {
        const layerResult = layerRequestsResult.states[index]?.result

        if (!layerResult) {
          return null
        }

        if (
          layer.space.childrenType === 'item' &&
          (layer.space.childrenItemView === 'heatmap' || layer.space.childrenItemView === 'cluster')
        ) {
          const style = hexagonStyles[index]
          const points = layerResult.items
            .map(({ value, h3 }: { value: number; h3: string }) => {
              if (h3) {
                return {
                  h3,
                  weight: value,
                  location: cellToMapLatLngLiteral(h3),
                  ...style.mapStyle({ value }),
                }
              }
            })
            .filter(truthy)
          return points
        }
        return null
      }),
    [layers, hexagonStyles, layerRequestsResult],
  )

  // Delimitations
  const delimitations = useMemo(
    () =>
      layers.map((layer, index) => {
        if (layer.space.delimitationType !== 'custom') {
          setStableCustomSpace(layer.space)
        }
        if (
          layer.space.childrenType === 'item' &&
          featureCollections[index]?.features[0]?.geometry &&
          (featureCollections[index]!.features[0].geometry.type === 'Polygon' ||
            featureCollections[index]!.features[0].geometry.type === 'MultiPolygon')
        ) {
          return featureCollections[index]!.features[0].geometry as
            | geojson.Polygon
            | geojson.MultiPolygon
            | undefined
        }
        return undefined
      }),
    [layers, featureCollections],
  )

  // Função de redimensionamento para o novo cluster
  const clusterPositions = useMemo(() => {
    return layers.map((layer, index): NeoClusterPosition[] | null => {
      const layerResult = layerRequestsResult.states[index]?.result
      if (!layerResult) {
        return null
      }
      if (
        layer.space.childrenType === 'item' &&
        (layer.space.childrenItemView === 'heatmap' || layer.space.childrenItemView === 'cluster')
      ) {
        const points = layerResult.items
          .map(
            ({
              value,
              h3,
              numerator,
              denominator,
            }: {
              value: number
              h3: string
              numerator: number
              denominator: number
            }) => {
              if (h3) {
                return {
                  weight: value,
                  realResolution: getResolution(h3),
                  h3: h3,
                  location: cellToMapLatLngLiteral(h3),
                  numerator: numerator,
                  denominator: denominator,
                }
              }
            },
          )
          .filter(truthy)

        return points
      }
      return null
    })
  }, [layers, layerRequestsResult])

  // Change base layers, clearing the selected insight
  const handleChangeBaseLayers = useCallback(
    (newBaseLayers: Layer[] | ((layers: Layer[]) => Layer[])) => {
      setBaseLayers(currentBaseLayers => {
        const resolvedBaseLayers =
          typeof newBaseLayers === 'function' ? newBaseLayers(currentBaseLayers) : newBaseLayers
        const normalizedBaseLayers = resolvedBaseLayers.map(normalizeLayerSpace)

        return normalizeLayersTimeAfterGeographyChange(
          currentBaseLayers,
          normalizedBaseLayers,
          getDefaultTimeForVariable,
        )
      })

      setSelectedInsight(null)
    },
    [normalizeLayerSpace, setBaseLayers, setSelectedInsight],
  )

  useEffect(() => {
    if (!baseLayers.length) {
      return
    }

    const normalizedBaseLayers = baseLayers.map(normalizeLayerSpace)
    if (JSON.stringify(normalizedBaseLayers) === JSON.stringify(baseLayers)) {
      return
    }

    setBaseLayers(normalizedBaseLayers)
    setSelectedInsight(null)
  }, [baseLayers, normalizeLayerSpace, setBaseLayers, setSelectedInsight])

  // Set initial layers
  useEffect(() => {
    if (!context.user || !variables.length) {
      return
    }
    // Run only once
    if (baseLayers.length) {
      return
    }

    const isNationalLevel = !isStateManager

    //TODO Configurar layer inicial para o estado do usuário, com alguma variável do usuário.
    const recommendedVariables = recommendedVariableForUser(context.user, variables)
    const recommendedLayer = recommendedLayerForVariable(
      recommendedVariables ? recommendedVariables : variables[0],
      isAdminUser,
      !isNationalLevel ? stateScope.stateId : null,
      !isNationalLevel ? stateScope.stateName : null,
      undefined,
      preferredSpace,
    )

    setBaseLayers([recommendedLayer])
  }, [context.user, variables, baseLayers, isStateManager, isAdminUser, stateScope, preferredSpace])

  // Map position
  const everythingIsLoaded = !layerRequestsResult.someIsLoading && !geoRequestsResult.someIsLoading

  const isReadyForSentinelUI = useMemo(
    () => Boolean(mapPosition && everythingIsLoaded && !variablesResult.isLoading),
    [mapPosition, everythingIsLoaded, variablesResult.isLoading],
  )

  const isInsightsModalReady = useMemo(() => Boolean(isReadyForSentinelUI), [isReadyForSentinelUI])

  // Após o primeiro carregamento completo, manter o modal independente de recarregamentos do mapa
  useEffect(() => {
    if (isInsightsModalReady && !isFirstLoadDone) {
      setIsFirstLoadDone(true)
    }
  }, [isInsightsModalReady, isFirstLoadDone])

  useEffect(() => {
    if (!everythingIsLoaded) return

    if (featureCollections.length) {
      const joinedFeatureCollection = featureCollections.reduce(
        (joinedFeatureCollection, featureCollection) => ({
          type: 'FeatureCollection' as 'FeatureCollection',
          features: [
            ...(joinedFeatureCollection!.features ?? []),
            ...(featureCollection?.features ?? []),
          ],
        }),
        {} as geojson.FeatureCollection,
      )
      const filteredFeatureCollection = {
        type: 'FeatureCollection' as 'FeatureCollection',
        features:
          // readySelectedInsight && readySelectedInsight.spaceChildrenIds?.length
          //   ? joinedFeatureCollection!.features.filter(
          //       feature =>
          //         readySelectedInsight.space.childrenType ===
          //           (feature as FeatureWithInfoAndStyle).properties.geoType &&
          //         readySelectedInsight.spaceChildrenIds!.includes(
          //           (feature as FeatureWithInfoAndStyle).properties.geoId!,
          //         ),
          //     )
          //   :
          joinedFeatureCollection!.features,
      }
      const featureCollection = filteredFeatureCollection.features.length
        ? filteredFeatureCollection
        : joinedFeatureCollection
      if (featureCollection.features.length) {
        const bbox = createBbox(featureCollection)
        const bounds: google.maps.LatLngBoundsLiteral = {
          west: bbox[0],
          south: bbox[1],
          east: bbox[2],
          north: bbox[3],
        }

        // If 3D and small area, focus on center
        const focusOnCenter =
          is3D && bounds.east - bounds.west < 0.05 && bounds.north - bounds.south < 0.05
        if (focusOnCenter) {
          const center = {
            lat: (bounds.north + bounds.south) / 2,
            lng: (bounds.east + bounds.west) / 2,
          }
          setMapPosition({ center, zoom: 16 })
          return
        }

        setMapPosition({ bounds })
        return
      }
    }

    if (!mapPosition) {
      if (hasAnyUserRole(context.user, ['systemAdministrator', 'administrator', 'manager'])) {
        setMapPosition({
          center: {
            lat: -10.742852346207142,
            lng: -53.07411538894531,
          },
          zoom: 6,
        })
        return
      } else {
        if (preferredMapCenter) {
          setMapPosition({
            center: preferredMapCenter,
            zoom: isStateManager ? 8 : 12,
          })
          return
        }
      }
    }
  }, [
    featureCollections,
    context.user,
    everythingIsLoaded,
    readySelectedInsight,
    is3D,
    preferredMapCenter,
    isAdminUser,
    isStateManager,
  ])

  // Conclusions per layer
  const conclusionsPerLayer = useRef<(Conclusion | null)[]>([])
  useEffect(() => {
    baseLayers.forEach((baseLayer, baseLayerIndex) => {
      const isBaseConclusions = baseLayer === layers[baseLayerIndex]
      if (isBaseConclusions && layerRequestsResult.states[baseLayerIndex]?.result) {
        conclusionsPerLayer.current[baseLayerIndex] =
          layerRequestsResult.states[baseLayerIndex].result.conclusion ?? null
      }
    })

    // Remove old conclusions, slice the array to the length of baseLayers
    conclusionsPerLayer.current = conclusionsPerLayer.current.slice(0, baseLayers.length)
  }, [baseLayers, layers, layerRequestsResult])

  // Info windows
  const infoWindows = useMemo(
    () =>
      valueFormatters.map((formatValue, layerIndex) => {
        return ({ feature }: { feature: FeatureWithInfoAndStyle }): JSX.Element => (
          <InfoWindow
            feature={feature}
            onChangeReport={setReport}
            layer={layers[layerIndex]}
            layers={layers}
            onChangeLayers={handleChangeBaseLayers}
            formatValue={formatValue}
          />
        )
      }),
    [valueFormatters, layers],
  )

  // const handleOpenInsightsModal = useCallback(() => {
  //   setIsInsightsModalOpen(true)
  // }, [])

  // const handleSelectInsightFromModal = useCallback(
  //   (selectedInsight: SelectedInsight) => {
  //     setSelectedInsight(selectedInsight)
  //     setIsInsightsModalOpen(false)
  //   },
  //   [setSelectedInsight],
  // )

  const openClusterList = useCallback(
    (
      feature: geojson.Feature<
        geojson.Polygon | geojson.MultiPolygon,
        {
          h3: string
          value: number
        }
      >,
      layer: Layer,
    ) => {
      const polygon = feature as unknown as geojson.Feature<
        geojson.Polygon | geojson.MultiPolygon,
        { hash: string }
      >
      polygon.properties.hash = feature.properties.h3

      const subLayer: Layer = {
        ...layer,
        space: {
          delimitationTitle: 'Agrupamento',
          delimitationType: 'custom',
          delimitationCustom: polygon,
          childrenType: 'item',
          childrenItemView: 'item',
          childrenTitle: 'Itens',
        },
      }
      setSelectedCluster({ layer: subLayer })
    },
    [resolutions, layers],
  )

  const handleClickCluster = useMemo(
    () =>
      memoize(
        (layer: Layer) =>
          (
            feature: geojson.Feature<
              geojson.Polygon | geojson.MultiPolygon,
              {
                h3: string
                value: number
              }
            >,
          ) => {
            openClusterList(feature, layer)
            return
          },
      ),
    [resolutions, layers],
  )

  // Toast errors
  const error =
    variablesResult.error || layerRequestsResult.someError || geoRequestsResult.someError

  useDebounce(
    () => {
      if (error) {
        const errorNotAbort = (
          [
            variablesResult.error,
            layerRequestsResult.someError,
            geoRequestsResult.someError,
          ] as (AppError | null)[]
        ).find(error => error && error.key !== 'abort')
        if (!errorNotAbort) {
          return
        }

        const finalError = fromUnknownError(errorNotAbort)
        toastError(finalError.message)
      }
    },
    500,
    [error],
  )

  const onChangeLegend = (index: number, range: { min: number; max: number }) => {
    const newData = [...styles]
    newData[index] = {
      ...newData[index],
      ...range,
    }

    setLegendStyles(newData)
  }

  const isLoadingSomething =
    layerRequestsResult.someIsLoading || geoRequestsResult.someIsLoading || isDebouncingInsight

  useEffect(() => {
    if (!isInsightsModalReady) return
    setIsInsightsModalOpen(current => current ?? true)
  }, [isInsightsModalReady])

  // Detecta mudança (variável / espaço) e agenda abertura
  useEffect(() => {
    const signature = `${JSON.stringify(baseLayers)}-${JSON.stringify(layers)}`
    if (signature !== lastVariableSpaceSignatureRef.current) {
      lastVariableSpaceSignatureRef.current = signature
      setPendingOpenInsightsModal(true) // aguarda carregamento
    }
  }, [baseLayers, layers])

  useEffect(() => {
    const shouldCloseInsights = layers.some(layer => {
      const key = layer.variable.key
      const preset = layer.variable.presetKey
      const fullKey = preset ? `${key}.${preset}` : key
      return (
        companyCategoryKeys.has(layer.variable.categoryKey as string) ||
        companyVariableKeys.has(fullKey)
      )
    })
    if (shouldCloseInsights) {
      skipNextAutoOpenRef.current = true
      setIsInsightsModalOpen(false)
      setPendingOpenInsightsModal(false)
      return
    }
    if (skipNextAutoOpenRef.current) {
      setPendingOpenInsightsModal(false)
      return
    }
    if (everythingIsLoaded) {
      setIsInsightsModalOpen(true)
      setPendingOpenInsightsModal(false)
      return
    }
    setPendingOpenInsightsModal(true)
  }, [
    layers,
    companyCategoryKeys,
    companyVariableKeys,
    everythingIsLoaded,
  ])

  // Abre após final do carregamento
  useEffect(() => {
    if (pendingOpenInsightsModal && everythingIsLoaded) {
      if (skipNextAutoOpenRef.current) {
        setPendingOpenInsightsModal(false)
        return
      } else {
        setIsInsightsModalOpen(true)
      }
      setPendingOpenInsightsModal(false)
    }
  }, [pendingOpenInsightsModal, everythingIsLoaded])

  const handleOpenInsightsModal = useCallback(() => {
    skipNextAutoOpenRef.current = false
    setIsInsightsModalOpen(true)
  }, [])

  const handleCloseInsightsModal = useCallback(() => {
    skipNextAutoOpenRef.current = true
    setIsInsightsModalOpen(false)
  }, [])

  return (
    <ScreenDefault.Root
      isLoadingFeature={isLoadingSomething}
      isLoadingScreen={variablesResult.isLoading}
      error={variablesResult.error}
    >
      <>
        <Handle isLoading={variablesResult.isLoading} error={variablesResult.error}>
          {mapPosition && (
            <Map
              mapProvider={
                is3D || mapType === 'roadmap' || mapType === 'terrain' ? 'mapbox' : 'google'
              }
              {...mapPosition}
              mapType={mapType}
              tilt={is3D ? 67.5 : 0}
              is3D={is3D}
            >
              {featureCollections.map((featureCollection, layerIndex) =>
                featureCollection &&
                (layers[layerIndex].space as SpaceChildrenItem).childrenItemView !== 'arc' ? (
                  <GeoJSON
                    key={String(layerIndex)}
                    geoJson={featureCollection}
                    idPropertyName='geoId'
                    renderInfoWindow={infoWindows[layerIndex]}
                  />
                ) : null,
              )}

              {featureCollections.map((featureCollection, layerIndex) =>
                featureCollection &&
                (layers[layerIndex].space as SpaceChildrenItem).childrenItemView === 'arc' ? (
                  <Arc
                    key={String(layerIndex)}
                    geoJson={featureCollection as FeatureCollectionWithInfoAndStyle}
                  />
                ) : null,
              )}

              {heatmapAndClusterWeightedPositions.map((positions, layerIndex) =>
                positions &&
                !layerRequestsResult.someIsLoading &&
                layers[layerIndex].space.childrenType === 'item' &&
                (layers[layerIndex].space as SpaceChildrenItem).childrenItemView === 'heatmap' ? (
                  <Heatmap
                    key={String(layerIndex)}
                    positions={positions}
                    resolution={resolutions[layerIndex]}
                    gradient={
                      layers[layerIndex].space.colorMap ??
                      getColorMap(
                        isVariableLayer(layers[layerIndex])
                          ? layers[layerIndex].variable.betterDensity ?? 'less'
                          : 'less',
                        'heatmap',
                      )
                    }
                  />
                ) : null,
              )}

              {/* Neo Cluster - Colmeia*/}
              {clusterPositions.map((positions, layerIndex) =>
                positions &&
                layers[layerIndex].space.childrenType === 'item' &&
                (layers[layerIndex].space as SpaceChildrenItem).childrenItemView === 'cluster' ? (
                  <NeoCluster
                    key={String(layerIndex)}
                    positions={positions}
                    layers={layers}
                    layerIndex={layerIndex}
                    onClickCluster={handleClickCluster(layers[layerIndex])}
                    onChangeLegend={onChangeLegend}
                    delimitation={delimitations[layerIndex]}
                    layerRequest={layersRequests[layerIndex]}
                  />
                ) : null,
              )}
            </Map>
          )}

          <ScreenDefault.Controls
            variables={variables}
            layers={layers}
            baseLayers={baseLayers}
            setBaseLayers={handleChangeBaseLayers}
            onChangeReport={setReport}
            stateId={stateScope.stateId}
            stateName={stateScope.stateName}
            subtitle={geoRequestsResult.states[0]?.result?.items[0]?.subtitle}
            onChangeMapType={setMapType}
            mapType={mapType!}
            onChangeMap3DOption={setMap3DOption}
            map3DOption={map3DOption!}
            styles={styles}
            isDisabled={!!selectedInsight}
            isInsightsModalOpen={
              (isInsightsModalOpen ?? false) && (isFirstLoadDone || isInsightsModalReady)
            }
            onOpenInsightsModal={handleOpenInsightsModal}
            isReadyForSentinelUI={isReadyForSentinelUI}
          >
            {!isLoadingSomething && <LegendsContainer layers={layers} styles={legendStyles} />}

            {report && (
              <MapReport
                onClose={() => setReport(null)}
                reportKey={report.reportKey}
                reportPayload={report.reportPayload}
                layer={report.layer}
              />
            )}
            {selectedCluster && (
              <ClusterList
                selectedCluster={selectedCluster}
                onClose={() => setSelectedCluster(null)}
              />
            )}
          </ScreenDefault.Controls>
        </Handle>

        {/* Modal de Insights */}
        {isFirstLoadDone || isInsightsModalReady ? (
          <InsightsModal
            isOpen={Boolean(isInsightsModalOpen ?? false)}
            onClose={handleCloseInsightsModal}
            selectedInsight={selectedInsight}
            onSelectInsight={setSelectedInsight}
            conclusionsPerLayer={conclusionsPerLayer.current}
            estimatedItemsCountPerLayer={estimatedItemsCountPerLayer}
            onChangeMapType={setMapType}
            mapType={mapType!}
            onChangeMap3DOption={setMap3DOption}
            map3DOption={map3DOption!}
            layers={layers}
            variables={variables}
            setBaseLayers={handleChangeBaseLayers}
            styles={styles}
            isLoading={isLoadingSomething}
          />
        ) : null}
      </>
    </ScreenDefault.Root>
  )
}
